import type { Player, Tool } from "../types";

export type ToolRequirement =
  | { kind: "level"; required: number }
  | { kind: "reputation"; required: number };

/**
 * The first requirement the player does not meet (level before reputation), or
 * null when the tool is unlocked. A tool without requirements is always unlocked.
 */
export function toolLockReason(tool: Tool, player: Player): ToolRequirement | null {
  if (player.level < (tool.minLevel ?? 1)) {
    return { kind: "level", required: tool.minLevel ?? 1 };
  }
  if (player.reputation < (tool.requiredReputation ?? 0)) {
    return { kind: "reputation", required: tool.requiredReputation ?? 0 };
  }
  return null;
}

/** Whether the player meets a tool's level/reputation requirements. */
export function isToolUnlocked(tool: Tool, player: Player): boolean {
  return toolLockReason(tool, player) === null;
}

/**
 * The tool's declared requirement for display (level before reputation), or null
 * when the tool has none. Independent of the player - used to show "met" state.
 */
export function toolRequirement(tool: Tool): ToolRequirement | null {
  if (tool.minLevel != null) {
    return { kind: "level", required: tool.minLevel };
  }
  if (tool.requiredReputation != null) {
    return { kind: "reputation", required: tool.requiredReputation };
  }
  return null;
}

/**
 * Whether a tool can be bought: it is unlocked, not already owned, and the
 * player's money covers its cost.
 */
export function canPurchaseTool(player: Player, tool: Tool): boolean {
  return (
    tool.status !== "upcoming" &&
    isToolUnlocked(tool, player) &&
    !player.ownedTools.includes(tool.id) &&
    player.money >= tool.cost
  );
}

/**
 * Returns a new player with the tool's cost deducted and the tool added to the
 * arsenal. Returns the input player unchanged when the purchase is not allowed.
 */
export function applyToolPurchase(player: Player, tool: Tool): Player {
  if (!canPurchaseTool(player, tool)) {
    return player;
  }
  return {
    ...player,
    money: player.money - tool.cost,
    ownedTools: [...player.ownedTools, tool.id],
  };
}
